/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/**
 * Utility pro práci s řetězci
 */
class StringUtil {
  /**
   * Porovná dva řetězce a vrátí, zda jsou shodné.
   * @see {@link Array.prototype.sort}
   * @see {@link Array.prototype.toSorted}
   * @param {string} a
   * @param {string} b
   * @returns {number} Vrátí negativní číslo, pokud je první argument menší jak
   * druhý argument, nulu pokud jsou stejné, jinak pozitivní číslo.
   */
  static compare(a, b) {
    if (StringUtil.isLess(a, b)) {
      return -1;
    }
    return Number(StringUtil.isLess(b, a));
  }

  /**
   * Vrátí všechny nalezeného výskytu hledané hodnoty.
   * Funguje stejně jako standardní `String#indexOf`, ale vrací všechny výskyty
   * a ne jen první.
   *
   * Zdroje:
   * - [Originální implementace](https://stackoverflow.com/a/14482123/8315965)
   * - [ECMAScript `String#indexOf`](https://tc39.es/ecma262/#sec-string.prototype.indexof)
   * - [MDN `String#indexOf`](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/indexOf)
   * @param {string} string Řetězec, ve kterém se hledají výskyty.
   * @param {string} searchValue Řetězec, jehož výskyty se hledají.
   * @param {Object} [opts = {}]
   * @param {number} [opts.max = Infinity] Maximální počet výskytů, který se hledá.
   * @returns {number[]}
   */
  static indexesOf(string, searchValue, { max = Infinity } = {}) {
    const { length } = string;
    const result = [];
    let index = -1;
    let countdown = max;
    // TODO Refaktorizovat originální zpracování bez ++ a --
    // eslint-disable-next-line no-plusplus
    while (countdown-- && index++ < length) {
      index = string.indexOf(searchValue, index);
      if (index === -1) {
        break;
      }
      result.push(index);
    }
    return result;
  }

  /**
   * Porovná dvě řetězce a vrátí, zda je a menší než b.
   * @param {string} a
   * @param {string} b
   * @returns {boolean}
   */
  static isLess(a, b) {
    return a < b;
  }

  /**
   * Porovná dvě řetězce, zda je a menší nebo rovno b.
   * @param {string} a
   * @param {string} b
   * @returns {boolean}
   */
  static isLessOrEqual(a, b) {
    return a <= b;
  }

  // TODO: Nikde se nepoužívá.
  /**
   * Vrátí index n-tého nalezeného výskytu hledané hodnoty.
   * Funguje stejně jako standardní `String#indexOf`, ale vrací n-tý výskyt
   * a ne jen první.
   *
   * Zdroje:
   * - [Originální implementace](https://stackoverflow.com/a/14482123/8315965)
   * - [ECMAScript `String#indexOf`](https://tc39.es/ecma262/#sec-string.prototype.indexof)
   * - [MDN `String#indexOf`](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/indexOf)
   * @param {string} string Řetězec, ve kterém se hledají výskyty.
   * @param {string} searchValue Řetězec, jehož výskyty se hledají.
   * @param {number} nth Pořadí výskytu, který se má hledat.
   * @returns {number}
   */
  static nthIndexOf(string, searchValue, nth) {
    const { length } = string;
    let index = -1;
    let countdown = nth;
    // TODO Refaktorizovat originální zpracování bez ++ a --
    // eslint-disable-next-line no-plusplus
    while (countdown-- && index++ < length) {
      index = string.indexOf(searchValue, index);
      if (index === -1) {
        break;
      }
    }
    return index;
  }
}

/**
 * @param {string} str
 * @param {string} prefix
 * @returns {string}
 */
export function trimPrefix(str, prefix) {
  if (str.startsWith(prefix)) {
    return str.slice(prefix.length);
  }
  return str;
}

/**
 * @param {string} str
 * @param {string} suffix
 * @returns {string}
 */
export function trimSuffix(str, suffix) {
  if (str.endsWith(suffix)) {
    return str.slice(0, -suffix.length);
  }
  return str;
}

export default StringUtil;
